import {
  assertFollowupSafetyPreflightV1,
  buildFollowupSafetyProofV1,
} from './followupSafetySnapshot';
import { sha256Hex } from './journeyState';
import { buildFollowupNutritionVersion } from './nutritionConsultationPersistence';

export const MANUAL_PROGRAM_UPDATE_SCOPES = Object.freeze([
  'targeted',
  'menus_recipes',
  'full',
]);

function readPlan(consultation) {
  return String(
    consultation?.nutritionPlan
      ?? consultation?.nutrition_plan
      ?? consultation?.plan_text
      ?? '',
  );
}

function assertScope(scope) {
  if (!MANUAL_PROGRAM_UPDATE_SCOPES.includes(scope)) {
    throw new Error('Portée de mise à jour invalide — à valider Anissa.');
  }
  return scope;
}

/**
 * Crée une nouvelle tête de programme, sans génération et sans publication.
 * Cette action est volontairement indépendante d'un rendez-vous : aucune
 * preuve clinique n'est empruntée ou transformée pour autoriser la version.
 */
export async function persistManualProgramUpdateVersion({
  currentForm,
  sourceConsultation,
  clientId,
  updateScope,
  versionNumber,
  persist,
} = {}) {
  if (typeof persist !== 'function') throw new Error('Service de sauvegarde indisponible.');
  const scope = assertScope(updateScope);
  const currentPlan = readPlan(sourceConsultation);
  if (!currentPlan.trim()) throw new Error('Le programme source complet est introuvable.');

  // Le précontrôle agrégé précède toute construction et toute persistance :
  // Anissa reçoit la liste complète des raccords à relire en une seule fois,
  // et aucun brouillon ne peut exister tant que l'anamnèse reste ambiguë.
  assertFollowupSafetyPreflightV1(currentForm);
  const safety = await buildFollowupSafetyProofV1(currentForm);
  const sourcePlanSha256 = await sha256Hex(currentPlan);
  const dateStr = new Date().toLocaleDateString('fr-CH', {
    day: '2-digit',
    month: 'short',
    year: '2-digit',
  });
  const candidate = buildFollowupNutritionVersion(sourceConsultation, {
    nutritionPlan: currentPlan,
    status: 'a_valider',
    label: `V${versionNumber} — Mise à jour du ${dateStr}`,
    consultantName: 'Anissa',
  }, {
    clientId,
    sourceConsultationId: sourceConsultation?.id,
    // Une demande hors rendez-vous est immédiate. Elle ne consomme et ne
    // prétend jamais représenter un jalon S4/S8/S12.
    followupWeek: 0,
    provenance: {
      trigger: 'manual_program_update',
      workflowAction: 'manual',
      updateScope: scope,
      sourcePlanSha256,
      safetySnapshotSha256: safety.sha256,
    },
  });
  return persist(candidate);
}
